import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { SelectionModel } from "@angular/cdk/collections";
import { CONSTANTS } from "../../../../../Constants/Constants";
import { MatDialog } from "@angular/material/dialog";
import { AddCollaboratorDialogComponent } from "../../../../dialogs/common/add-collaborator-dialog/add-collaborator-dialog.component";
import { ICollaborationFormGroup } from "../../../../../models/properties/ICollaborationFormGroup";
import { CollaboratorDetails, CollaborationState } from "../../../../../models/dtos";
import { CommonDataService } from "../../../../../services/common-data.service";
import { take } from "rxjs";
import { UserProfileService } from "../../../../../services/common/user-profile.service";
import { GetReviewDetailsService } from "../../../../../services/api/get-review-details.service";
import { MultilingualCurrencyPipe } from "../../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { CostReviewFunctionsService } from '../../../../../services/review/cost-review-functions.service';
import { VerifyCollaboratorsDialogComponent } from "../../../../dialogs/common/verify-collaborators-dialog/verify-collaborators-dialog.component";
import { CollaborationOperationsService } from "../../../../../services/common/collaboration-operations.service";
import { NotificationMessageService } from "../../../../../services/common/notification-message.service";
import { IAddCollaboratorDialogData } from "../../../../../models/preferences/IAddCollaboratorDialogData";
import { IVerifyMultipleCollaborators } from "../../../../../models/collaboration/IVerifyMultipleCollaborators";
import { emailsMatch } from "../../../../../services/collab-common-functions";

@Component({
    selector: 'app-sol-properties-estimate-collaboration',
    templateUrl: './sol-properties-estimate-collaboration.component.html',
    styleUrls: ['./sol-properties-estimate-collaboration.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatSlideToggleModule,
        MatTableModule,
        ReactiveFormsModule,
        FormsModule,
        MatButtonModule,
        MatSelectModule,
        MatTooltipModule,
        TranslatePipe
    ],
    providers: [MultilingualCurrencyPipe]
})
export class SolPropertiesEstimateCollaborationComponent implements OnInit {
    // Define the displayed columns for the table
    displayedColumns: string[] = ['role', 'collaborator', 'email', 'access'];

    // Define the data source for the table
    dataSource = new MatTableDataSource<CollaboratorDetails>();
    selection = new SelectionModel<CollaboratorDetails>(false, []);
    collaborationForm: FormGroup<ICollaborationFormGroup>;
    collaboratorList: CollaboratorDetails[] = [];
    private destroyRef = inject(DestroyRef);
    private previousCollaborativeState: CollaborationState = CollaborationState.Open;
    private isCollaborationViewMode = false;

    constructor(private readonly fb: NonNullableFormBuilder,
        readonly solPropertiesService: SolPropertiesService,
        private readonly dialogRef: MatDialog,
        private readonly commonDataService: CommonDataService,
        private readonly userProfileService: UserProfileService,
        private readonly reviewDetailsService: GetReviewDetailsService,
        private readonly translateService: TranslateService,
        private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe,
        private readonly costReviewOperationsService: CostReviewFunctionsService,
        private readonly collaborationOperationsService: CollaborationOperationsService,
        private readonly notificationMessageService: NotificationMessageService) {
    }

    ngOnInit(): void {
        // logged user name should be pushed to the primaryUsers array. right now kept as empty string
        this.initializeForm();
        this.getCollaboratorList();
        this.patchDataSource();
        this.patchPrimaryUserValue();
        this.fetchPrimaryUserCostReviewInfo();
        this.subscribeFormValueChanges();
        this.setupReadOnlyMode();
        this.subscribeToCollaborationRevert();
    }

    private subscribeToCollaborationRevert(): void {
        this.solPropertiesService.collaborationStateReverted$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(reverted => {
                if (reverted) {
                    this.collaborationForm.controls.collaborationEnabled.setValue(!this.collaborationForm.controls.collaborationEnabled.value, { emitEvent: false });
                }
            });
    }

    private subscribeFormValueChanges(): void {
        this.collaborationForm.valueChanges
            .pipe(take(CONSTANTS.ONE))
            .subscribe(
                () => this.solPropertiesService.markCollaborationChanged()
            );
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.updateCollaborationViewMode();
            });
    }

    private initializeForm(): void {
        const collaborationState = this.solPropertiesService.getCollaborationData().collaborationState;
        this.previousCollaborativeState = collaborationState === CollaborationState.NonCollaborative
            ? CollaborationState.Open
            : collaborationState;

        this.collaborationForm = this.fb.group({
            collaborationEnabled: this.fb.control<boolean>(collaborationState !== CollaborationState.NonCollaborative),
            primaryUser: this.fb.control<string>(CONSTANTS.EMPTY_STRING),
            costReviewThreshold: this.fb.control<string>(CONSTANTS.EMPTY_STRING),
            costReviewOptionalWorkflow: this.fb.control<string>(CONSTANTS.EMPTY_STRING),
            addCollaborator: this.fb.control<boolean>(true),
            removeAllCollaborators: this.fb.control<boolean>(true),
            removeSelectedCollaborator: this.fb.control<boolean>(true),
            collaboratorRowSelection: this.fb.control<boolean>(true),
            collaboratorAccess: this.fb.control<boolean>(true)
        });

        this.registerCollaborationToggleHandler();
        this.updateCollaborationUiState();
    }

    isCollaborationEnabled(): boolean {
        return this.collaborationForm.controls.collaborationEnabled.value;
    }

    canEditCollaboration(): boolean {
        return this.isCollaborationEnabled() && this.isCurrentUserOwner() && !this.commonDataService.isReadOnlyMode();
    }

    private isCurrentUserOwner(): boolean {
        const currentUser = this.collaboratorList.find(collaborator => collaborator.email === this.userProfileService.userInfo?.mail && collaborator.roleId === CONSTANTS.COLLABORATION_OWNER_ROLE_ID);
        return currentUser !== undefined;
    }

    onRowClick(row: CollaboratorDetails): void {
        if (this.collaborationForm.controls.collaboratorRowSelection.disabled) {
            return;
        }

        this.selection.toggle(row);
        this.syncCollaborationActionControls();
    }

    private registerCollaborationToggleHandler(): void {
        this.collaborationForm.controls.collaborationEnabled.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isEnabled => {
                const collaborationData = this.solPropertiesService.getCollaborationData();

                if (isEnabled) {
                    collaborationData.collaborationState = this.previousCollaborativeState;
                } else {
                    if (collaborationData.collaborationState !== CollaborationState.NonCollaborative) {
                        this.previousCollaborativeState = collaborationData.collaborationState;
                    }
                    collaborationData.collaborationState = CollaborationState.NonCollaborative;
                }

                this.updateCollaborationUiState();
                this.updateCollaborationViewMode();
            });
    }

    private updateCollaborationUiState(): void {
        if (this.isCollaborationEnabled()) {
            this.collaborationForm.controls.primaryUser.enable({ emitEvent: false });
            this.syncCollaborationActionControls();
            return;
        }

        this.selection.clear();
        this.collaborationForm.controls.primaryUser.disable({ emitEvent: false });
        this.syncCollaborationActionControls();
    }

    private syncCollaborationActionControls(): void {
        const disableActions = this.isCollaborationViewMode || !this.isCollaborationEnabled();

        this.setControlDisabled(this.collaborationForm.controls.collaboratorRowSelection, disableActions);
        this.setControlDisabled(this.collaborationForm.controls.collaboratorAccess, disableActions);
        this.setControlDisabled(this.collaborationForm.controls.addCollaborator, disableActions || !this.enableAddCollaborator());
        this.setControlDisabled(this.collaborationForm.controls.removeAllCollaborators, disableActions || !this.hasVisibleCollaborators());
        this.setControlDisabled(this.collaborationForm.controls.removeSelectedCollaborator, disableActions || this.selection.isEmpty());
    }

    private setControlDisabled(control: AbstractControl, disabled: boolean): void {
        if (disabled && control.enabled) {
            control.disable({ emitEvent: false });
        } else if (!disabled && control.disabled) {
            control.enable({ emitEvent: false });
        }
    }

    getCollaboratorList() {
        const collaborationDataList = this.solPropertiesService.getCollaborationData().collaboratorDataList;

        if (collaborationDataList.length > 0) {
            this.collaboratorList = collaborationDataList
                .map(collaborator => ({
                    ...collaborator,
                    role: this.mapRoleIdToRoleName(collaborator.roleId)
                }));
        } else {
            const preferenceCollaborators: CollaboratorDetails[] =
                structuredClone(this.commonDataService.getPreferenceCollaborators());

            this.collaboratorList = preferenceCollaborators.map(pref => ({
                name: pref.name,
                email: pref.email,
                roleId: pref.roleId,
                canEdit: pref.canEdit,
                isPrimaryUser: pref.isPrimaryUser,
                role: this.mapRoleIdToRoleName(pref.roleId)
            }));
        }

        this.ensureOwnerExists();
        this.solPropertiesService.setInitialCollaborators(this.collaboratorList);
        this.solPropertiesService.setCollaboratorList(this.collaboratorList);
    }

    private ensureOwnerExists(): void {
        const ownerRoleId = CONSTANTS.ZERO;
        const hasOwner = this.collaboratorList.some(c => c.roleId === ownerRoleId);
        if (!hasOwner) {
            const currentUser = this.userProfileService.userInfo;
            const ownerEntry: CollaboratorDetails = {
                name: currentUser?.name ?? CONSTANTS.EMPTY_STRING,
                email: currentUser?.mail ?? CONSTANTS.EMPTY_STRING,
                roleId: ownerRoleId,
                canEdit: true,
                isPrimaryUser: true,
                role: this.mapRoleIdToRoleName(ownerRoleId)
            };
            this.collaboratorList.unshift(ownerEntry);
        }
    }

    trackByEmail(index: number, collaborator: CollaboratorDetails): string {
        return collaborator.email;
    }


    patchDataSource() {
        this.dataSource.data = this.collaboratorList;
    }

    mapRoleIdToRoleName(roleId: number): string {
        const role = this.solPropertiesService.getRolesMasterData().find(role => role.id === roleId);
        return role ? role.description : '';
    }

    patchPrimaryUserValue() {
        const primaryUser = this.collaboratorList.find(collaborator => collaborator.isPrimaryUser);
        if (primaryUser) {
            this.collaborationForm.controls.primaryUser.setValue(primaryUser.email);
        }
    }

    private getAvailableReviewers(): CollaboratorDetails[] {
        const availableCollaborators = this.commonDataService.getAllCollaboratorPreferences()?.availableCollaborators ?? [];
        return availableCollaborators.filter(collaborator =>
            !emailsMatch(collaborator.email, this.userProfileService.userInfo?.mail) &&
            !this.collaboratorList.some(existing => emailsMatch(existing.email, collaborator.email))
        );
    }

    addCollaborator(): void {
        if (this.collaborationForm.controls.addCollaborator.disabled) {
            return;
        }

        const dialogData: IAddCollaboratorDialogData = {
            collaboratorRoles: this.solPropertiesService.getRolesMasterData(),
            excludedCollaboratorRoleIds: this.solPropertiesService.getExcludedCollaboratorRoleIds(),
            alreadyAddedCollaborators: this.collaboratorList,
            isCalledFromPreferences: false
        };

        const dialog = this.dialogRef.open(AddCollaboratorDialogComponent, {
            width: '500px',
            data: dialogData,
            disableClose: true
        });

        dialog.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(result => {
            if (result) {
                this.addCollaboratorToState(result);
                this.syncCollaborationActionControls();
            }
        });
    }

    enableAddCollaborator(): boolean {
        return this.collaborationOperationsService.canEnableAddCollaborator(this.collaboratorList);
    }


    private addCollaboratorToState(collaboratorData: CollaboratorDetails): void {
        const isDuplicate = this.collaboratorList.some(c => emailsMatch(c.email, collaboratorData.email));
        if (isDuplicate) {
            return;
        }

        const collaborationData = this.solPropertiesService.getCollaborationData();
        collaborationData.collaboratorDataList.push(collaboratorData);

        const collaboratorTableData: CollaboratorDetails = {
            ...collaboratorData,
            role: this.mapRoleIdToRoleName(collaboratorData.roleId)
        };

        this.collaboratorList.push(collaboratorTableData);
        this.solPropertiesService.setCollaboratorList(this.collaboratorList);

        this.dataSource.data = this.collaboratorList;
    }

    toggleBtnClicked(collaborator: CollaboratorDetails): void {
        if (this.collaborationForm.controls.collaboratorAccess.disabled) {
            return;
        }

        collaborator.canEdit = !collaborator.canEdit;
    }

    hasVisibleCollaborators(): boolean {
        return this.collaboratorList.length > CONSTANTS.ONE;
    }

    removeAllCollaborator(): void {
        if (this.collaborationForm.controls.removeAllCollaborators.disabled) {
            return;
        }

        const emailsToRemove = new Set(this.dataSource.data.map(collaborator => collaborator.email && collaborator.email !== this.userProfileService.userInfo?.mail ? collaborator.email : CONSTANTS.EMPTY_STRING).filter(email => email !== CONSTANTS.EMPTY_STRING));
        this.removeFromOpenEstimateData(emailsToRemove);
        this.collaboratorList = this.collaboratorList.reduce<CollaboratorDetails[]>((acc, collaborator) => {
            if (collaborator.roleId === 0) {
                acc.push({ ...collaborator, role: this.mapRoleIdToRoleName(collaborator.roleId) });
            }
            return acc;
        }, []);
        this.solPropertiesService.setCollaboratorList(this.collaboratorList);
        this.dataSource.data = this.collaboratorList;
        this.selection.clear();

        if (!this.hasVisibleCollaborators()) {
            this.collaborationForm.controls.collaborationEnabled.setValue(false);
        }
        this.syncCollaborationActionControls();
    }

    removeSelectedCollaborator(): void {
        if (this.collaborationForm.controls.removeSelectedCollaborator.disabled) {
            return;
        }

        const selectedCollaborator = this.selection.selected[0];
        const emailsToRemove = new Set([selectedCollaborator.email]);

        this.removeFromOpenEstimateData(emailsToRemove);
        this.collaboratorList = this.collaboratorList.filter(
            collaborator => collaborator.email !== selectedCollaborator.email
        );
        this.solPropertiesService.setCollaboratorList(this.collaboratorList);
        this.dataSource.data = this.dataSource.data.filter(
            collaborator => collaborator.email !== selectedCollaborator.email
        );
        this.selection.clear();

        if (!this.hasVisibleCollaborators()) {
            this.collaborationForm.controls.collaborationEnabled.setValue(false);
        }
        this.syncCollaborationActionControls();
    }

    private removeFromOpenEstimateData(emailsToRemove: Set<string>): void {
        const collaborationData = this.solPropertiesService.getCollaborationData();
        collaborationData.collaboratorDataList = collaborationData.collaboratorDataList.filter(
            collaborator => collaborator.isPrimaryUser || !emailsToRemove.has(collaborator.email)
        );

    }

    async collaborationEnabledToggled(): Promise<void> {
        const isEnabled = this.collaborationForm.controls.collaborationEnabled.value;

        if (!isEnabled) {
            return;
        }

        const emails: string[] = this.collaboratorList?.map(c => c.email) ?? [];

        if (emails.length === CONSTANTS.ZERO) {
            return;
        }

        try {
            const emailListwithoutCurrentUser = emails.filter(email => email !== this.userProfileService.userInfo?.mail);
            const availableCollaborators: CollaboratorDetails[] = this.commonDataService.getAllCollaboratorPreferences()?.availableCollaborators ?? [];
            const invalidCollaborators = await this.collaborationOperationsService.verifyMultipleCollaborators(emailListwithoutCurrentUser, availableCollaborators);


            if (invalidCollaborators.collaborators.length > CONSTANTS.ZERO || invalidCollaborators.availableCollaborators.length > CONSTANTS.ZERO) {

                let invalid: IVerifyMultipleCollaborators = {
                    collaborators: invalidCollaborators.collaborators,
                    availableCollaborators: invalidCollaborators.availableCollaborators
                }
                const dialogRef = this.dialogRef.open(VerifyCollaboratorsDialogComponent, {
                    data: invalid,
                    disableClose: true
                });
                dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(shouldProceed => {
                    if (this.collaboratorList.length > invalidCollaborators.collaborators.length) {
                        this.collaboratorList = this.collaboratorList.filter(c => !invalidCollaborators.collaborators.some(i => i.collaboratorEmail === c.email));
                        this.solPropertiesService.setCollaboratorList(this.collaboratorList);
                        this.dataSource.data = this.collaboratorList;
                    } else {
                        this.revertCollaborationToggle();
                    }
                });
            }
        } catch (err) {
            this.revertCollaborationToggle();
        }
    }

    // README: Need to verify, if logic is still reuired or not
    // private async modifyCollaboration(): Promise<void> {
    //     const estimateSaveRequest = await this.commonFunctionsService.createSaveEstimateRequest();
    //     this.collaborationOperationsService.modifyCollaboration(this.collaboratorList, CONSTANTS.EMPTY_STRING, estimateSaveRequest)
    //     .then(result => {
    //             if (!result) {
    //                 this.revertCollaborationToggle();
    //             }
    //         })
    //         .catch(() => this.revertCollaborationToggle());
    // }

    private revertCollaborationToggle(): void {
        this.collaborationForm.controls.collaborationEnabled.setValue(false, { emitEvent: false });
        this.solPropertiesService.getCollaborationData().collaborationState = CollaborationState.NonCollaborative;
        this.updateCollaborationUiState();
        this.updateCollaborationViewMode();
    }

    get costReviewThreshold(): string {
        return this.collaborationForm.controls.costReviewThreshold.value;
    }

    set costReviewThreshold(value: string) {
        this.collaborationForm.controls.costReviewThreshold.setValue(value, { emitEvent: false });
    }

    get costReviewOptionalWorkflow(): string {
        return this.collaborationForm.controls.costReviewOptionalWorkflow.value;
    }

    set costReviewOptionalWorkflow(value: string) {
        this.collaborationForm.controls.costReviewOptionalWorkflow.setValue(value, { emitEvent: false });
    }

    fetchPrimaryUserCostReviewInfo(): void {
        const primaryUserEmail = this.collaborationForm.controls.primaryUser.value;
        if (!primaryUserEmail) {
            return;
        }

        const payload = this.costReviewOperationsService.buildUserCostReviewThresholdRequest(primaryUserEmail);
        this.reviewDetailsService.getUserCostReviewThreshold(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (response.threshold === -1) {
                        this.costReviewThreshold = this.translateService.instant('NOT_APPLICABLE');
                        this.costReviewOptionalWorkflow = this.translateService.instant('NOT_APPLICABLE');
                    } else {
                        this.costReviewThreshold = this.multilingualCurrencyPipe.transform(response.threshold);
                        this.costReviewOptionalWorkflow = response.isCostReviewUnderThreshold
                            ? this.translateService.instant('YES')
                            : this.translateService.instant('NO');
                    }
                },
                error: () => {
                    this.costReviewThreshold = CONSTANTS.EMPTY_STRING;
                    this.costReviewOptionalWorkflow = CONSTANTS.EMPTY_STRING;
                }
            });
    }

    // Persists the selected primary user onto the staged collaborator lists so Save publishes
    // the updated isPrimaryUser flag. Cancel is unaffected because these mutations only touch
    // the cloned data owned by SolPropertiesService.
    onPrimaryUserChange(): void {
        const selectedEmail = this.collaborationForm.controls.primaryUser.value;
        if (!selectedEmail) {
            return;
        }

        this.collaboratorList.forEach(collaborator => {
            collaborator.isPrimaryUser = collaborator.email === selectedEmail;
        });
        this.solPropertiesService.setCollaboratorList(this.collaboratorList);

        const collaborationData = this.solPropertiesService.getCollaborationData();
        collaborationData.collaboratorDataList.forEach(collaborator => {
            collaborator.isPrimaryUser = collaborator.email === selectedEmail;
        });

        this.solPropertiesService.markCollaborationChanged();
        this.fetchPrimaryUserCostReviewInfo();
    }

    getUserDisplayName(name: string): string {
        return name.split('(')[0].split(", ").join(" ");
    }

    private updateCollaborationViewMode(): void {
        this.isCollaborationViewMode = this.commonDataService.isReadOnlyMode() || (this.isCollaborationEnabled() && !this.isCurrentUserOwner());
        if (this.isCollaborationViewMode) {
            this.collaborationForm.disable({ emitEvent: false });
            this.syncCollaborationActionControls();
        } else {
            this.collaborationForm.enable({ emitEvent: false });
            this.updateCollaborationUiState();
        }
    }
}
